// Pestaña «Métricas» (Carranza, 16-oct-2026): cuántos llegan de cada ciudad, día a día y por semana.
// Lee portal_metricas() (conteos sin datos personales). Toda la cuenta vive en src/reclutamiento/metricas.ts.
import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { ArrowDown, ArrowUp, ChevronsUpDown, Info } from "lucide-react";
import { CIUDADES, ciudadPorClave, type ClaveCiudad } from "@/reclutamiento/ciudades-metricas";
import {
  avancePorSemana,
  ciudadesConDatos,
  ETAPAS_AVANCE,
  ETIQUETA_MEDIDA,
  esPeriodo,
  filasDe,
  MEDIDAS,
  ordenar,
  PERIODOS,
  porcentajePasaron,
  rangoAnterior,
  rangoDe,
  totalesPorCiudad,
  diaLegible,
  type Agrupacion,
  type Conteo,
  type Fila,
  type Medida,
  type Orden,
  type Periodo,
} from "@/reclutamiento/metricas";
import { fechaAmigable } from "@/reclutamiento/logica";
import { CajaError, Esqueleto } from "../componentes";
import { usePortal } from "../contexto";
import { IconoPlaza } from "../etapas";
import { claveCache, guardarPref, leerPref, useCarga } from "../util";
import { claseCiudad } from "./metricas-colores";

const GraficaMetricas = lazy(() => import("./GraficaMetricas"));

// Siempre se ven estas cuatro (aunque estén en cero); Edomex, otra y sin ciudad solo si traen algo.
const SIEMPRE: ClaveCiudad[] = ["monterrey", "queretaro", "cdmx", "cancun"];

/** ¿El portal está en tema oscuro? (lo pone tema.ts en <html data-theme>) */
function useOscuro(): boolean {
  const leer = () => typeof document !== "undefined" && document.documentElement.dataset.theme === "dark";
  const [o, setO] = useState(leer);
  useEffect(() => {
    const mo = new MutationObserver(() => setO(leer()));
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
    return () => mo.disconnect();
  }, []);
  return o;
}

export function Metricas() {
  const { api, sesion } = usePortal();
  const carga = useCarga(() => api.metricas(), [api], undefined, claveCache(sesion, api.verComo, "metricas"));
  const oscuro = useOscuro();

  const [periodo, setPeriodo] = useState<Periodo>(() => {
    const p = leerPref("metricas-periodo");
    return esPeriodo(p) ? p : "15";
  });
  const [agrupacion, setAgrupacion] = useState<Agrupacion>(() => (leerPref("metricas-agrupacion") === "semana" ? "semana" : "dia"));
  const [medida, setMedida] = useState<Medida>("llegaron");
  const [orden, setOrden] = useState<Orden>({ col: "fecha", asc: false });
  const [ciudadAvance, setCiudadAvance] = useState<ClaveCiudad | null>(null);

  const elegirPeriodo = (p: Periodo) => {
    guardarPref("metricas-periodo", p);
    setPeriodo(p);
  };
  const elegirAgrupacion = (a: Agrupacion) => {
    guardarPref("metricas-agrupacion", a);
    setAgrupacion(a);
  };

  const m = carga.datos;
  const primera = useMemo(() => (m?.dias.length ? m.dias.reduce((x, d) => (d.fecha < x ? d.fecha : x), m.dias[0].fecha) : null), [m]);
  const rango = useMemo(() => (m ? rangoDe(periodo, m.hoy, primera) : null), [m, periodo, primera]);
  const totales = useMemo(() => (m && rango ? totalesPorCiudad(m.dias, rango) : null), [m, rango]);
  const anterior = useMemo(() => {
    const r = rango ? rangoAnterior(periodo, rango) : null;
    return m && r ? totalesPorCiudad(m.dias, r).total : null;
  }, [m, rango, periodo]);
  const conDatos = useMemo(() => (m && rango ? ciudadesConDatos(m.dias, rango) : []), [m, rango]);
  const tarjetas = useMemo(() => CIUDADES.map((c) => c.clave).filter((c) => SIEMPRE.includes(c) || conDatos.includes(c)), [conDatos]);
  // En la gráfica y en la tabla: solo ciudades con algo en el periodo (si no hay nada, las cuatro de siempre).
  const series = conDatos.length ? conDatos : SIEMPRE;
  const filas = useMemo(() => (m && rango ? filasDe(m.dias, rango, periodo === "hoy" ? "dia" : agrupacion, null) : []), [m, rango, agrupacion, periodo]);
  const avance = useMemo(() => (m && rango ? avancePorSemana(m.etapas, rango, ciudadAvance) : []), [m, rango, ciudadAvance]);
  const agr: Agrupacion = periodo === "hoy" ? "dia" : agrupacion;

  if (carga.cargando && !m)
    return (
      <>
        <Encabezado />
        <Esqueleto filas={3} alto={140} />
      </>
    );
  if (carga.error || !m || !rango || !totales)
    return (
      <>
        <Encabezado />
        <CajaError
          texto={
            /portal_metricas|could not find the function/i.test(carga.error ?? "")
              ? "Las métricas todavía no están activas en tu portal. Avísanos y las prendemos."
              : (carga.error ?? "No pudimos cargar las métricas.")
          }
          onReintentar={carga.recargar}
        />
      </>
    );

  const valorFila = (f: Fila, col: string): number | string =>
    col === "fecha" ? f.clave : col === "total" ? f.total[medida] : f.porCiudad[col as ClaveCiudad][medida];
  const filasTabla = ordenar(filas, valorFila, orden);
  const sinNada = totales.total.llegaron + totales.total.pasaron + totales.total.descartados === 0;
  const textoPeriodo = periodo === "hoy" ? `hoy, ${diaLegible(rango.desde)}` : `del ${diaLegible(rango.desde)} al ${diaLegible(rango.hasta)}`;

  return (
    <>
      <Encabezado actualizado={m.actualizado_en} />

      <div className="grupos mt-periodos" role="group" aria-label="Periodo">
        {PERIODOS.map(([p, l]) => (
          <button key={p} type="button" aria-pressed={periodo === p} onClick={() => elegirPeriodo(p)}>
            {l}
          </button>
        ))}
      </div>
      <p className="mt-rango muted">
        Viendo {textoPeriodo}
        {periodo === "todo" && primera ? " (desde el primer dato)" : ""}.
      </p>

      {sinNada && !m.dias.length ? (
        <p className="empty">Todavía no llegan conteos a tu portal. En cuanto se sincronicen (cada 5 minutos) aparecen aquí.</p>
      ) : (
        <>
          <section className="mt-cards" aria-label="Totales por ciudad">
            <TarjetaTotal c={totales.total} anterior={anterior} periodo={periodo} />
            {tarjetas.map((c) => (
              <TarjetaCiudad key={c} ciudad={c} c={totales.porCiudad[c]} />
            ))}
          </section>

          <section className="vcard mt-sec" aria-labelledby="mt-graf-t">
            <div className="mt-sec-h">
              <h2 id="mt-graf-t">{agr === "semana" ? "Por semana (lunes a domingo)" : "Día por día"}</h2>
              {periodo !== "hoy" && (
                <div className="modes mt-dos" role="group" aria-label="Agrupar">
                  <button type="button" aria-pressed={agrupacion === "dia"} onClick={() => elegirAgrupacion("dia")}>
                    Por día
                  </button>
                  <button type="button" aria-pressed={agrupacion === "semana"} onClick={() => elegirAgrupacion("semana")}>
                    Por semana
                  </button>
                </div>
              )}
            </div>
            <div className="modes mt-medidas" role="group" aria-label="Qué contar">
              {MEDIDAS.map((x) => (
                <button key={x} type="button" aria-pressed={medida === x} onClick={() => setMedida(x)}>
                  {ETIQUETA_MEDIDA[x]}
                </button>
              ))}
            </div>
            <ul className="mt-leyenda" aria-label="Colores por ciudad">
              {series.map((c) => (
                <li key={c} className={claseCiudad(c)}>
                  <i aria-hidden="true" />
                  {ciudadPorClave(c).corta}
                </li>
              ))}
            </ul>
            <Suspense fallback={<div className="sk" style={{ height: 260 }} />}>
              <GraficaMetricas filas={filas} medida={medida} ciudades={series} oscuro={oscuro} agrupacion={agr} />
            </Suspense>

            <h3 className="mt-tabla-t">
              {ETIQUETA_MEDIDA[medida]} {agr === "semana" ? "por semana" : "por día"} y ciudad
              <span className="muted"> · toca un encabezado para ordenar</span>
            </h3>
            <div className="mt-tabla-scroll">
              <table className="mt-tabla">
                <thead>
                  <tr>
                    <Th col="fecha" orden={orden} setOrden={setOrden}>
                      {agr === "semana" ? "Semana" : "Día"}
                    </Th>
                    {series.map((c) => (
                      <Th key={c} col={c} orden={orden} setOrden={setOrden} num>
                        {ciudadPorClave(c).corta}
                      </Th>
                    ))}
                    <Th col="total" orden={orden} setOrden={setOrden} num>
                      Total
                    </Th>
                  </tr>
                </thead>
                <tbody>
                  {filasTabla.map((f) => (
                    <tr key={f.clave}>
                      <th scope="row">
                        {f.etiqueta}
                        {f.parcial && <span className="muted"> (parcial)</span>}
                      </th>
                      {series.map((c) => (
                        <td key={c} className={"num" + (f.porCiudad[c][medida] ? "" : " cero")}>
                          {f.porCiudad[c][medida]}
                        </td>
                      ))}
                      <td className="num tot">{f.total[medida]}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <th scope="row">Total</th>
                    {series.map((c) => (
                      <td key={c} className="num">
                        {totales.porCiudad[c][medida]}
                      </td>
                    ))}
                    <td className="num tot">{totales.total[medida]}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </section>
        </>
      )}

      <section className="vcard mt-sec" aria-labelledby="mt-av-t">
        <div className="mt-sec-h">
          <h2 id="mt-av-t">Tu avance por semana</h2>
        </div>
        <p className="muted mt-nota">Cuántos candidatos moviste a cada etapa, semana por semana (lunes a domingo).</p>
        <div className="plazas" role="group" aria-label="Ciudad del avance">
          <span className="plazas-t">Ciudad</span>
          <button type="button" className="c-todos" aria-pressed={!ciudadAvance} onClick={() => setCiudadAvance(null)}>
            <IconoPlaza size={17} strokeWidth={2.4} aria-hidden="true" className="pin" />
            Todas
          </button>
          {tarjetas.map((c) => (
            <button key={c} type="button" className={claseCiudad(c)} aria-pressed={ciudadAvance === c} onClick={() => setCiudadAvance(c)}>
              <IconoPlaza size={17} strokeWidth={2.4} aria-hidden="true" className="pin" />
              {ciudadPorClave(c).corta}
            </button>
          ))}
        </div>
        <div className="mt-tabla-scroll">
          <table className="mt-tabla">
            <thead>
              <tr>
                <th scope="col">Semana</th>
                {ETAPAS_AVANCE.map(([k, l]) => (
                  <th key={k} scope="col" className="der">
                    {l}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {avance.map((s) => (
                <tr key={s.lunes}>
                  <th scope="row">
                    {s.etiqueta}
                    {s.parcial && <span className="muted"> (parcial)</span>}
                    {s.aproximado && <span title="Incluye movimientos de antes del registro completo"> *</span>}
                  </th>
                  {ETAPAS_AVANCE.map(([k]) => (
                    <td key={k} className={"num" + (s.n[k] ? "" : " cero")}>
                      {s.n[k]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="muted mt-nota">
          <Info size={15} aria-hidden="true" />{" "}
          {m.registro_etapas_desde
            ? `Desde el ${diaLegible(fechaMty(m.registro_etapas_desde))} se cuenta cada movimiento. `
            : "El registro de cada movimiento arranca con esta pestaña. "}
          Antes solo se guardaba el último movimiento de cada candidato: esas semanas llevan * y pueden quedarse cortas.
        </p>
      </section>

      <details className="vcard mt-que">
        <summary>¿Qué cuenta cada número?</summary>
        <ul>
          <li>
            <b>Llegaron</b>: personas que le escribieron al bot por primera vez ese día.
          </li>
          <li>
            <b>Pasaron el filtro</b>: el bot leyó su CV y lo aprobó (cumple o «a revisar») ese día.
          </li>
          <li>
            <b>Descartados</b>: el bot leyó su CV y no cumple los requisitos. Una persona puede llegar un día y pasar otro.
          </li>
          <li>
            <b>Ciudad</b>: la que escribió la persona. Estado de México y Toluca van aparte de la Ciudad de México.
          </li>
          <li>Días en hora de Monterrey. Se actualiza cada 5 minutos.</li>
        </ul>
      </details>
    </>
  );
}

const DIA_MTY = new Intl.DateTimeFormat("en-CA", { timeZone: "America/Monterrey", year: "numeric", month: "2-digit", day: "2-digit" });
const fechaMty = (ts: string) => (/^\d{4}-\d{2}-\d{2}$/.test(ts) ? ts : DIA_MTY.format(new Date(ts)));

function Encabezado({ actualizado }: { actualizado?: string | null }) {
  const { sesion } = usePortal();
  return (
    <div className="phello">
      <p className="eyebrow">{sesion.cliente.nombre}</p>
      <h1>Métricas</h1>
      <p>
        Cuántos llegan de cada ciudad.
        {actualizado ? ` Actualizado ${fechaAmigable(actualizado, null)}.` : ""}
      </p>
    </div>
  );
}

function Th({
  col,
  orden,
  setOrden,
  num,
  children,
}: {
  col: string;
  orden: Orden;
  setOrden: (o: Orden) => void;
  num?: boolean;
  children: React.ReactNode;
}) {
  const activa = orden.col === col;
  const I = !activa ? ChevronsUpDown : orden.asc ? ArrowUp : ArrowDown;
  return (
    <th scope="col" className={num ? "der" : undefined} aria-sort={activa ? (orden.asc ? "ascending" : "descending") : "none"}>
      <button type="button" onClick={() => setOrden({ col, asc: activa ? !orden.asc : false })}>
        {children}
        <I size={14} aria-hidden="true" />
      </button>
    </th>
  );
}

function TarjetaTotal({ c, anterior, periodo }: { c: Conteo; anterior: Conteo | null; periodo: Periodo }) {
  const dif = anterior ? c.llegaron - anterior.llegaron : null;
  const contra = periodo === "hoy" ? "ayer" : periodo === "30" ? "los 30 días anteriores" : `los ${periodo} días anteriores`;
  return (
    <article className="mt-card c-todos mt-total">
      <h2>Total</h2>
      <p className="mt-big">
        <span className="num">{c.llegaron}</span> llegaron
      </p>
      <Desglose c={c} />
      {dif !== null && (
        <p className="mt-vs">
          {dif === 0 ? "Igual que" : dif > 0 ? `${dif} más que` : `${-dif} menos que`} {contra}
        </p>
      )}
    </article>
  );
}

function TarjetaCiudad({ ciudad, c }: { ciudad: ClaveCiudad; c: Conteo }) {
  return (
    <article className={"mt-card " + claseCiudad(ciudad)}>
      <h2>
        <IconoPlaza size={18} strokeWidth={2.4} aria-hidden="true" className="pin" />
        {ciudadPorClave(ciudad).corta}
      </h2>
      <p className="mt-big">
        <span className="num">{c.llegaron}</span> llegaron
      </p>
      <Desglose c={c} />
    </article>
  );
}

function Desglose({ c }: { c: Conteo }) {
  return (
    <dl className="mt-des">
      <div>
        <dt>Pasaron</dt>
        <dd className="num">{c.pasaron}</dd>
      </div>
      <div>
        <dt>Descartados</dt>
        <dd className="num">{c.descartados}</dd>
      </div>
      <div>
        <dt>% pasaron</dt>
        <dd className="num">{porcentajePasaron(c)}</dd>
      </div>
    </dl>
  );
}
